/**
 * Exercise name parsing utilities
 * Parse exercise names like "Bench Press (Barbell)" into name and equipment_type
 */

import { EQUIPMENT_TYPES } from '../data/equipment-types.js'

/**
 * Equipment type mapping from various formats to our standard types
 */
const EQUIPMENT_MAP = {
  'Barbell': EQUIPMENT_TYPES.BARBELL,
  'Dumbbell': EQUIPMENT_TYPES.DUMBBELL,
  'Smith Machine': EQUIPMENT_TYPES.SMITH_MACHINE,
  'Cable': EQUIPMENT_TYPES.CABLE,
  'Machine': EQUIPMENT_TYPES.MACHINE,
  'Band': EQUIPMENT_TYPES.BAND,
  'Kettlebell': EQUIPMENT_TYPES.KETTLEBELL,
  'Bodyweight': EQUIPMENT_TYPES.BODYWEIGHT,
  // Common variations
  'Dumbbells': EQUIPMENT_TYPES.DUMBBELL,
  'Cables': EQUIPMENT_TYPES.CABLE,
  'Resistance Band': EQUIPMENT_TYPES.BAND,
  'Body Weight': EQUIPMENT_TYPES.BODYWEIGHT,
}

/**
 * Parse exercise name from "Name (Equipment)" format
 * @param {string} fullName - Full exercise name like "Bench Press (Barbell)"
 * @returns {{ name: string, equipment_type: string|null }}
 */
export function parseExerciseName(fullName) {
  if (!fullName || typeof fullName !== 'string') {
    return { name: '', equipment_type: null }
  }

  const trimmed = fullName.trim()

  // Try to match "Name (Equipment)" pattern
  const match = trimmed.match(/^(.+?)\s*\((.+?)\)$/)

  if (match) {
    const [, name, equipmentStr] = match
    return {
      name: name.trim(),
      equipment_type: parseEquipmentType(equipmentStr.trim())
    }
  }

  // No equipment in parentheses, return as-is
  return {
    name: trimmed,
    equipment_type: null
  }
}

/**
 * Parse equipment type string to our standard equipment_type
 * @param {string} str - Equipment string from parentheses
 * @returns {string|null}
 */
export function parseEquipmentType(str) {
  if (!str) return null

  // Try exact match first
  if (EQUIPMENT_MAP[str]) {
    return EQUIPMENT_MAP[str]
  }

  // Try case-insensitive match
  const normalized = str.trim()
  const lowerStr = normalized.toLowerCase()

  for (const [key, value] of Object.entries(EQUIPMENT_MAP)) {
    if (key.toLowerCase() === lowerStr) {
      return value
    }
  }

  // No match found, return null
  console.warn(`Unknown equipment type: "${str}"`)
  return null
}

/**
 * Infer muscle group from exercise name (simple heuristics)
 * @param {string} name - Exercise name
 * @returns {string} - Muscle group (chest, back, shoulders, arms, legs, core, cardio, other)
 */
export function inferMuscleGroup(name) {
  if (!name) return 'other'

  const lower = name.toLowerCase()

  // Chest keywords
  if (lower.includes('bench') || lower.includes('press') && (lower.includes('chest') || lower.includes('pec')) || lower.includes('fly') || lower.includes('push up') || lower.includes('pushup')) {
    return 'chest'
  }

  // Back keywords
  if (lower.includes('pull') && !lower.includes('push') || lower.includes('row') || lower.includes('deadlift') || lower.includes('lat')) {
    return 'back'
  }

  // Shoulder keywords
  if (lower.includes('shoulder') || lower.includes('overhead') || lower.includes('lateral') || lower.includes('front raise') || lower.includes('rear delt')) {
    return 'shoulders'
  }

  // Arms keywords
  if (lower.includes('curl') || lower.includes('tricep') || lower.includes('bicep') || lower.includes('hammer') || lower.includes('skull crusher')) {
    return 'arms'
  }

  // Legs keywords
  if (lower.includes('squat') || lower.includes('leg') || lower.includes('lunge') || lower.includes('calf') || lower.includes('deadlift') && lower.includes('romanian')) {
    return 'legs'
  }

  // Core keywords
  if (lower.includes('plank') || lower.includes('crunch') || lower.includes('sit up') || lower.includes('ab') || lower.includes('core') || lower.includes('twist')) {
    return 'core'
  }

  // Cardio keywords
  if (lower.includes('run') || lower.includes('cycle') || lower.includes('row') && lower.includes('machine') || lower.includes('cardio') || lower.includes('treadmill')) {
    return 'cardio'
  }

  return 'other'
}

/**
 * Create exercise object from parsed name and equipment
 * @param {string} fullName - Full exercise name
 * @param {object} options - Additional options
 * @returns {object} - Exercise object ready for database
 */
export function createExerciseFromName(fullName, options = {}) {
  const { name, equipment_type } = parseExerciseName(fullName)

  return {
    name,
    equipment_type,
    muscleGroup: options.muscleGroup || inferMuscleGroup(name),
    description: options.description || '',
    isCustom: options.isCustom !== undefined ? options.isCustom : true,
  }
}
